import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, Info, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ShiftMarketplaceRequest } from '../../types';
import { marketplaceSchedulerBadge } from '../../utils/shiftMarketplacePending';
import { MarketplaceRequestDetails, marketplaceStatusMeta } from './MarketplaceRequestDetails';

const APPROVALS_PATH = '/shift-marketplace?tab=approvals';

export function MarketplaceCoverDetailsModal({
  request,
  onClose,
}: {
  request: ShiftMarketplaceRequest;
  onClose: () => void;
}) {
  const { employee } = useAuth();
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const status = marketplaceStatusMeta(request.status);
  const statusOverride =
    request.status === 'PendingApproval' || request.status === 'Accepted'
      ? { ...status, label: 'Pending approval' }
      : status;

  const modal = (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl mx-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="marketplace-cover-details-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 z-10 rounded-md p-1.5 hover:bg-white/80 text-neutral-500"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <MarketplaceRequestDetails
            request={request}
            viewerEmployeeId={employee?.id}
            variant="detailed"
            statusOverride={statusOverride}
            subtitle={marketplaceSchedulerBadge(request)}
            titleId="marketplace-cover-details-title"
          >
            <div className="flex justify-end pt-1">
              <a
                href={APPROVALS_PATH}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-900"
              >
                Marketplace
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            </div>
          </MarketplaceRequestDetails>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

export function MarketplaceCoverBadge({
  request,
  variant = 'onColor',
}: {
  request: ShiftMarketplaceRequest;
  variant?: 'onColor' | 'onLight';
}) {
  const [open, setOpen] = useState(false);
  const label = marketplaceSchedulerBadge(request);

  return (
    <>
      <div
        className={`mt-1 flex items-center gap-0.5 text-[10px] font-semibold leading-tight px-1 py-0.5 rounded ${
          variant === 'onColor' ? 'bg-amber-100 text-amber-900' : 'bg-amber-50 text-amber-800'
        }`}
      >
        <span className="min-w-0 truncate">{label}</span>
        <button
          type="button"
          title="View cover details"
          aria-label="View cover details"
          className="flex-shrink-0 p-0.5 rounded hover:bg-amber-200 text-amber-900"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpen(true);
          }}
        >
          <Info className="w-3 h-3" />
        </button>
      </div>
      {open && (
        <MarketplaceCoverDetailsModal request={request} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
